import { useStore } from 'src/contexts/StoreContext';
import { icon } from 'src/theme/colors';
import { ICON_NAMES } from '../icon/icon.helper';
import { useCallback, useMemo } from 'react';
import { BOTTOM_SHEET_VIEWS } from 'src/store/bottom-sheet/bottomSheet.constants';
import { ConfigurationStepId } from 'src/helpers/configurationSteps.helper';
import { ROUTE_NAMES } from 'src/routes/_shared/routes.constants';
import { useTypedNavigation } from 'src/editor-v3/hooks/useTypedNavigation';
import { useTypedRoute } from 'src/editor-v3/hooks/useTypedRoute';
import amplitude from 'src/_shared/amplitude';
import { AnalyticsEvents } from 'src/_shared/analyticsEvents';

export const useBSSharePhotos = () => {
  const {
    i18n,
    bottomSheet,
    toastStore,
    project: { groupCode, startAddingPhotosFlow, addProductsV2, activeGroup, activeProject, productConfigurationId },
    data,
  } = useStore();

  const navigation = useTypedNavigation();
  const route = useTypedRoute<ROUTE_NAMES.Upload>();
  const params = route.params ?? {};
  const { groupCodeExternal, skipConfigurationStep, skipStepValue, ...filteredParams } = params;

  const isUploadRoute = useMemo(() => [ROUTE_NAMES.Upload].includes(route?.name as ROUTE_NAMES), [route.name]);

  const onPressConfirm = useCallback(async () => {
    data.setShowNewImageNotification(false);
    bottomSheet.closeBottomSheet();
    setTimeout(() => {
      bottomSheet.openBottomSheet({
        bottomSheetView: BOTTOM_SHEET_VIEWS.PhotoDrawer,
        showDragIndicator: false,
        contentViewStyle: { paddingHorizontal: 0 },
        showOpacityBackground: false,
        uploadButtonCallback: async () => {
          bottomSheet.closeBottomSheet();
          if (isUploadRoute) {
            const id = await activeProject?.getDefaultProductIdByGroup(activeGroup);
            activeGroup?.checkMinMaxProductsAndNotify();
            addProductsV2({
              ids: activeProject?.selectedThumbnails.map(t => t.originalImageId) ?? [],
              productConfigurationId:
                toastStore.data || id || activeGroup?.currectProductConfigurationId || productConfigurationId,
              groupCode: activeGroup?.code || groupCode,
              groupId: activeGroup?.groupIdSDK,
            });
            toastStore.setData(undefined);
          } else {
            startAddingPhotosFlow({
              initialProductConfigurationId: activeGroup
                ? activeGroup.productConfigurationIdDefault
                : productConfigurationId,
              groupCode: activeGroup ? activeGroup.code : groupCode,
              skipConfigurationSteps: [ConfigurationStepId.BOX_STYLE],
              groupId: activeGroup?.id,
              fileIds: activeProject?.selectedThumbnails.map(t => t.originalImageId),
            });
          }
        },
      });
    }, 500);

    amplitude.trackEvent(AnalyticsEvents.sharePhoto.addedClick, {
      action: 'accept',
      position: isUploadRoute ? 'picker' : 'personalize',
    });
    if (isUploadRoute) navigation.navigate(ROUTE_NAMES.EditorNew, { ...filteredParams });
  }, []);

  const onPressDecline = useCallback(async () => {
    toastStore.showInfoToast({
      message: i18n.t('toast:photos-added'),
      iconColor: icon.iconPrimary,
      id: 'toast-incognito-hint',
      removeOnPress: false,
      onPress: () => {},
      icon: ICON_NAMES.PhotoAlbum,
      duration: 3000,
    });
    data.setShowNewImageNotification(false);
    bottomSheet.closeBottomSheet();
  }, []);
  return {
    i18n,
    data,
    onPressConfirm,
    onPressDecline,
  };
};
